import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { isWarehouseView } from "@/utils/permissions";
import {
  Menu,
  ArrowLeft,
  Route,
  ChevronRight,
  Truck,
} from "lucide-react";

const PAGE_SIZE = 30;

export default function DeliveryRoundsPage() {
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();
  const [rounds, setRounds] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Margini bulk per i giri visibili (solo admin)
  const [margins, setMargins] = useState({});
  const isAdmin = user?.role === "admin";

  const isWarehouse = isWarehouseView(user);
  const basePath = isWarehouse ? "/warehouse" : user?.role === "admin" ? "/admin" : "/agent";

  const fetchPage = async ({ reset = false } = {}) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    try {
      const skip = reset ? 0 : rounds.length;
      const res = await authAxios.get("/delivery-rounds/paginated", {
        params: { limit: PAGE_SIZE, skip },
      });
      const data = res.data || {};
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
      setRounds((prev) => (reset ? data.items || [] : [...prev, ...(data.items || [])]));
    } catch {
      toast.error("Errore nel caricamento giri");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchPage({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAxios]);

  // Fetch bulk margini quando cambia la lista (solo admin)
  useEffect(() => {
    if (!isAdmin || !rounds.length) return;
    const ids = rounds.map((r) => r.id).filter((id) => !(id in margins));
    if (!ids.length) return;
    authAxios.get("/delivery-rounds/margins", { params: { ids: ids.join(",") } })
      .then((r) => setMargins((prev) => ({ ...prev, ...r.data })))
      .catch(() => { /* silent */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rounds, isAdmin]);

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="delivery-rounds-page">
      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Giri Consegne" />
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" className="text-zinc-400 hover:text-white" onClick={() => navigate(`${basePath}/orders`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Ordini
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <Route className="w-7 h-7 text-orange-400" />
              Giri Consegne
            </h1>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="skeleton h-20 rounded-xl" />)}
          </div>
        ) : rounds.length === 0 ? (
          <Card className="bg-[#18181b] border-white/5">
            <CardContent className="p-10 text-center">
              <Route className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400 text-sm">Nessun giro consegne creato</p>
              <p className="text-zinc-500 text-xs mt-1">Seleziona ordini in preparazione e clicca "Crea Giro Consegne"</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-1.5">
            {rounds.map((r) => (
              <Card
                key={r.id}
                data-testid={`round-card-${r.id}`}
                className={`shadow-md shadow-black/40 hover:shadow-lg hover:shadow-black/50 hover:-translate-y-px transition-all cursor-pointer ${
                  r.bbb_exported_at
                    ? "bg-emerald-950/50 border-emerald-500/30 hover:border-emerald-400/50"
                    : "bg-zinc-800 border-zinc-700 hover:border-zinc-600"
                }`}
                onClick={() => navigate(`${basePath}/delivery-rounds/${r.id}`)}
              >
                <CardContent className="p-2.5">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${r.bbb_exported_at ? "bg-emerald-500/15" : "bg-orange-500/10"}`}>
                        <span className={`font-mono font-bold text-base ${r.bbb_exported_at ? "text-emerald-400" : "text-orange-400"}`}>{r.letter}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">
                          Giro {r.letter}{r.name ? ` ${r.name}` : ""} del {r.date
                            ? new Date(r.date + "T00:00:00").toLocaleDateString("it-IT")
                            : new Date(r.created_at).toLocaleDateString("it-IT")}
                        </p>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                          <span className="text-xs text-zinc-500 flex items-center gap-1">
                            <Truck className="w-3 h-3" />
                            {r.deliveries?.length || 0} consegne
                          </span>
                          {r.created_by_name && (
                            <span className="text-xs text-zinc-500">da {r.created_by_name}</span>
                          )}
                          {r.bbb_exported_at && (
                            <span className="text-[10px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-1.5 py-0.5 rounded-full font-mono">
                              BBB ✓
                            </span>
                          )}
                          {isAdmin && margins[r.id] && margins[r.id].tot_revenue > 0 && (() => {
                            const m = margins[r.id];
                            const color = m.margin_pct >= 30 ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
                                        : m.margin_pct >= 15 ? "text-amber-300 bg-amber-500/10 border-amber-500/30"
                                        : "text-red-400 bg-red-500/10 border-red-500/30";
                            return (
                              <span
                                data-testid={`round-margin-${r.id}`}
                                title={`Ricavi €${m.tot_revenue.toFixed(2)} · Costo €${m.tot_cost.toFixed(2)} · Margine €${m.margin_eur.toFixed(2)}`}
                                className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-full border ${color}`}
                              >
                                Marg. {m.margin_pct.toFixed(1)}% (€{m.margin_eur.toFixed(0)})
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-600" />
                  </div>
                </CardContent>
              </Card>
            ))}
            {hasMore && (
              <div className="pt-3 flex justify-center">
                <Button
                  data-testid="rounds-load-more-btn"
                  onClick={() => fetchPage()}
                  disabled={loadingMore}
                  variant="outline"
                  className="border-white/10 bg-[#18181b] text-zinc-200 hover:bg-white/5 hover:text-white"
                >
                  {loadingMore
                    ? "Caricamento..."
                    : `⬇ Carica altri ${Math.min(PAGE_SIZE, total - rounds.length)} giri`}
                </Button>
              </div>
            )}
            {!hasMore && rounds.length > 0 && (
              <p className="text-center text-xs text-zinc-600 pt-3" data-testid="rounds-end-marker">
                Fine elenco — {total} giri totali
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
